import { useState } from "react";
import { useMutation, useQuery, useIsMutating } from "@tanstack/react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faX,
  faCheckCircle,
  faXmarkCircle,
  faIndianRupeeSign,
} from "@fortawesome/free-solid-svg-icons";
import { withAppHOC } from "../../hoc";
import {
  StyledContentContainer,
  StyledFiltersContainer,
  StyledFilters,
  StyledFilterContainer,
  StyledSearchButton,
  StyledResetButton,
  StyledStatusPill,
  StyledActionItemButtons,
  StyledModalContainer,
  StyledFormContainer,
} from "../../components/styled";
import PageHeader from "../../components/PageHeader/PageHeader";
import {
  MessageBox,
  Dropdown,
  TextInput,
  CustomConfirmButton,
  Form,
  Fieldset,
  Label,
  Button,
  CustomButton,
} from "../../components/FormElements";
import Modal from "../../components/Modal";
import Loader from "../../components/Loader/Loader";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import NoData from "../../components/NoData/NoData";
import { TableBuilder } from "../../components/TableElements";
import Pagination from "../../components/Pagination/Pagination";
import API from "../../api";
import { RESPONSE_STATUSES } from "../../constants";
import { formatCurrency, getSearchParams } from "../../utils";
import FiltersButton from "../../components/FiltersButton/FiltersButton";

const SellerWrongToRightTransactionsList = () => {
  const [showFilters, setShowFilters] = useState(false);
  const onClickShowFilters = () => {
    setShowFilters((showFilters) => !showFilters);
  };
  const [filters, setFilters] = useState({
    fTransactionsId: "",
    fRechargeNumber: "",
    fStatus: "",
    fFromDate: "",
    fToDate: "",
  });
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [modalType, setModalType] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [item, setItem] = useState(null);
  const [operatorsId, setOperatorsId] = useState("");
  const [remark, setRemark] = useState("");
  const [isLoadingUpdate, setIsLoadingUpdate] = useState(false);
  const [responseStatus, setResponseStatus] = useState("");
  const [responseMessage, setResponseMessage] = useState("");
  const [paginationData, setPaginationData] = useState({
    pageNumber: getSearchParams("pageNumber") || 1,
    recordsPerPage: getSearchParams("recordsPerPage") || 30,
  });

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const updateTransaction = async (e) => {
    e.preventDefault();
    setIsLoadingUpdate(true);
    setResponseStatus("");
    setResponseMessage("");
    API.put(`/seller/wrong-to-right-transactions/${item.id}`, {
      action: modalType,
      operatorsId,
      remark,
    })
      .then((response) => {
        const { status, message } = response.data;
        setResponseStatus(status);
        setResponseMessage(message);
        if (status === RESPONSE_STATUSES.SUCCESS) {
          reloadData();
          setTimeout(() => {
            setShowModal(false);
            setItem(null);
            setRemark("");
            setOperatorsId("");
          }, 3000);
        }
      })
      .catch((error) => {
        setResponseStatus(RESPONSE_STATUSES.FAILURE);
        setResponseMessage(error.message);
      })
      .finally(() => {
        setIsLoadingUpdate(false);
      });
  };

  const refundTransaction = async (item) => {
    try {
      const response = await API.put(`/seller/transactions/refund/${item?.transactionsId}`);
      return response.data;
    } catch (error) {
      throw new Error(error);
    }
  };

  const readData = async () => {
    try {
      const response = await API.get(
        `/seller/wrong-to-right-transactions?pageNumber=${paginationData.pageNumber}&recordsPerPage=${paginationData.recordsPerPage}&fTransactionsId=${appliedFilters.fTransactionsId}&fRechargeNumber=${appliedFilters.fRechargeNumber}&fFromDate=${appliedFilters.fFromDate}&fToDate=${appliedFilters.fToDate}&fStatus=${appliedFilters.fStatus}`
      );
      return response.data;
    } catch (error) {
      throw new Error(error);
    }
  };

  const { data, isLoading, error, refetch, isRefetching } = useQuery({
    queryKey: [
      "seller_wrong_to_right_transactions",
      paginationData,
      appliedFilters,
    ],
    queryFn: readData,
  });

  const { mutateAsync: refundTransactionMutation } = useMutation({
    mutationFn: refundTransaction,
    onError: (error) => console.error(error),
    onSuccess: () => {
      reloadData();
    },
  });

  const isMutation = useIsMutating();

  const reloadData = async () => await refetch();

  const filterData = async (e) => {
    e.preventDefault();
    setAppliedFilters(filters);
  };

  const clearFilters = async (e) => {
    e.preventDefault();
    setFilters({
      fTransactionsId: "",
      fRechargeNumber: "",
      fStatus: "",
      fFromDate: "",
      fToDate: "",
    });
    setAppliedFilters({
      fTransactionsId: "",
      fRechargeNumber: "",
      fStatus: "",
      fFromDate: "",
      fToDate: "",
    });
  };

  const renderPageHeading = () => (
    <PageHeader
      title="Wrong To Right Transactions"
      showAddButton={false}
      showReloadData={true}
      reloadData={reloadData}
    />
  );

  const statusOptions = [
    {
      name: "All",
      value: "",
    },
    {
      name: "Pending",
      value: "Pending",
    },
    {
      name: "Approved",
      value: "Approved",
    },
    {
      name: "Rejected",
      value: "Rejected",
    },
  ];

  if (isLoading) {
    return (
      <>
        {renderPageHeading()}
        <StyledContentContainer>
          <LoadingSpinner />
        </StyledContentContainer>
      </>
    );
  }

  if (error) {
    return (
      <>
        {renderPageHeading()}
        <StyledContentContainer>
          <MessageBox
            status={RESPONSE_STATUSES.FAILED}
            message={error.message}
          />
        </StyledContentContainer>
      </>
    );
  }

  return (
    <>
      <Loader isLoading={isMutation > 0 || isRefetching} />
      {renderPageHeading()}
      <FiltersButton onClick={onClickShowFilters} value={showFilters} />
      <StyledContentContainer graybackground="true">
        <StyledFiltersContainer className={showFilters ? "show" : ""}>
          <StyledFilters>
            <StyledFilterContainer>
              <TextInput
                value={filters.fTransactionsId}
                onChange={(value) =>
                  handleFilterChange("fTransactionsId", value)
                }
                placeholder="Transaction ID"
                disabled={isLoading}
              />
              <TextInput
                value={filters.fRechargeNumber}
                onChange={(value) =>
                  handleFilterChange("fRechargeNumber", value)
                }
                placeholder="Recharge Number"
                disabled={isLoading}
              />
              <Dropdown
                value={filters.fStatus}
                onChange={(value) => handleFilterChange("fStatus", value)}
                placeholder="Status"
                disabled={isLoading}
                options={statusOptions}
              />
              <TextInput
                type="date"
                value={filters.fFromDate}
                onChange={(value) => handleFilterChange("fFromDate", value)}
                placeholder="From Date"
                disabled={isLoading}
              />
              <TextInput
                type="date"
                value={filters.fToDate}
                onChange={(value) => handleFilterChange("fToDate", value)}
                placeholder="To Date"
                disabled={isLoading}
              />
            </StyledFilterContainer>
            <StyledSearchButton onClick={filterData} type="button">
              <FontAwesomeIcon icon={faSearch} />
              Search
            </StyledSearchButton>
            <StyledResetButton onClick={clearFilters} type="button">
              <FontAwesomeIcon icon={faX} />
              Clear
            </StyledResetButton>
          </StyledFilters>
        </StyledFiltersContainer>
        {data?.status === RESPONSE_STATUSES.FAILED && (
          <MessageBox
            status={RESPONSE_STATUSES.FAILED}
            message={data?.message}
          />
        )}
        {data?.data?.length === 0 ? (
          <NoData />
        ) : (
          <>
            <TableBuilder
              isLoading={isLoading}
              tableHeadings={[
                {
                  title: "Transaction ID",
                  dataSelector: "transactionsId",
                  dataType: "string",
                  textAlign: "center",
                },
                {
                  title: "Wrong Account Number",
                  dataSelector: "wrongAccountNumber",
                  dataType: "string",
                },
                {
                  title: "Reason",
                  dataSelector: "reason",
                  dataType: "string",
                },
                {
                  title: "Buyers Remark",
                  dataSelector: "remark",
                  dataType: "string",
                  cellrenderer: (value) => value ?? "N/A",
                },
                {
                  title: "Right Account Number",
                  dataSelector: "rightAccountNumber",
                  dataType: "string",
                },
                {
                  title: "Operator Id",
                  dataSelector: "operatorId",
                  dataType: "string",
                  cellrenderer: (value) => value ?? "N/A",
                },
                {
                  title: "Amount",
                  dataSelector: "amount",
                  dataType: "string",
                  cellrenderer: (value) => formatCurrency(value),
                },
                {
                  title: "Remark",
                  dataSelector: "sellersRemark",
                  dataType: "string",
                  cellrenderer: (value) => value ?? "N/A",
                },
                {
                  title: "Status",
                  dataSelector: "status",
                  dataType: "string",
                  textAlign: "center",
                  cellrenderer: (value) => (
                    <StyledStatusPill
                      className={
                        value === "Approved"
                          ? "green"
                          : value === "Rejected"
                          ? "red"
                          : "yellow"
                      }
                    >
                      {value}
                    </StyledStatusPill>
                  ),
                },
                {
                  title: "Complaint Raised Time",
                  dataSelector: "createdOn",
                  dataType: "string",
                  cellrenderer: (value) => (value ? value : "N/A"),
                },
                {
                  title: "Complaint Resolution Time",
                  dataSelector: "updatedOn",
                  dataType: "string",
                  cellrenderer: (value) => (value ? value : "N/A"),
                },
                {
                  title: "",
                  dataSelector: "id",
                  canSort: false,
                  isSticky: "Yes",
                  stickyAlign: "right",
                  isDummy: "Yes",
                  textAlign: "center",
                  width: "max-content",
                  cellrenderer: (value, item) => (
                    <StyledActionItemButtons>
                      {item.status === "Pending" && (
                        <>
                          <CustomButton
                            icon={faCheckCircle}
                            style={{color: 'green'}}
                            onClick={() => {
                              setItem(item);
                              setModalType("Approved");
                              setShowModal(true);
                            }}
                          />
                          <CustomButton
                            icon={faXmarkCircle}
                            style={{color: 'red'}}
                            onClick={() => {
                              setItem(item);
                              setModalType("Rejected");
                              setShowModal(true);
                            }}
                          />
                          <CustomConfirmButton
                            icon={faIndianRupeeSign}
                            title="Refund"
                            message="Are you sure, you want to refund this transaction ?"
                            proceedButtonText="Refund"
                            cancelButtonText="Cancel"
                            color="forestgreen"
                            hoverColor="#006200"
                            showTitle={false}
                            onClick={async () =>
                              await refundTransactionMutation({...item})
                            }
                          />
                        </>
                      )}
                    </StyledActionItemButtons>
                  ),
                },
              ]}
              tableData={data?.data}
            />
            <Pagination
              totalRecords={data?.totalRecords}
              paginationData={paginationData}
              setPaginationData={setPaginationData}
            />
            <Modal
              modalStatus={showModal}
              setModalStatus={() => {
                setShowModal(false);
                setModalType("");
                setItem("");
              }}
              hideCloseButton
            >
              <StyledModalContainer width={`500px`}>
                <h3>
                  {modalType === "Approved"
                    ? `Approve Request`
                    : `Reject Request`}
                </h3>

                {(modalType === "Approved" || modalType === "Rejected") && (
                  <StyledFormContainer>
                    <Form method="POST" action="#" onSubmit={updateTransaction}>
                      {modalType === "Approved" && (
                        <Fieldset>
                          <Label required>Operator ID</Label>
                          <TextInput
                            value={operatorsId}
                            onChange={setOperatorsId}
                            placeholder="Please enter operators id"
                            disabled={isLoadingUpdate}
                          />
                        </Fieldset>
                      )}
                      <Fieldset>
                        <Label required>Remark</Label>
                        <TextInput
                          value={remark}
                          onChange={setRemark}
                          placeholder="Please enter remark"
                          disabled={isLoadingUpdate}
                        />
                      </Fieldset>
                      {showModal && responseStatus !== "" && (
                        <Fieldset>
                          <MessageBox
                            status={responseStatus}
                            message={responseMessage}
                          />
                        </Fieldset>
                      )}
                      <Fieldset>
                        <Button
                          disabled={isLoadingUpdate}
                          type="submit"
                          style={{ width: "auto" }}
                        >
                          Submit
                        </Button>
                      </Fieldset>
                      <Loader isLoading={isLoadingUpdate} />
                    </Form>
                  </StyledFormContainer>
                )}
              </StyledModalContainer>
            </Modal>
          </>
        )}
      </StyledContentContainer>
    </>
  );
};

export default withAppHOC(SellerWrongToRightTransactionsList);
